import { useState } from 'react';
import { SceneItem } from './VideoScriptEditor';

interface TemplateScene {
  description: string;
  cameraAngle: string;
  duration: string;
  mood: string;
  transition: string;
}

interface PromptTemplate {
  id: string;
  name: string;
  category: string;
  categoryColor: string;
  icon: string;
  description: string;
  sceneCount: number;
  scenes: TemplateScene[];
}

const TEMPLATES: PromptTemplate[] = [
  {
    id: 'product-launch',
    name: 'Product Launch',
    category: 'Marketing',
    categoryColor: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    icon: 'ri-rocket-line',
    description: 'Dramatic reveal sequence with hero shot, feature highlights, lifestyle context and CTA finale',
    sceneCount: 4,
    scenes: [
      { description: 'Black void — a single ray of light slowly reveals the product silhouette on a dark reflective surface, particles of light swirling around it, dramatic tension building', cameraAngle: 'Wide Shot', duration: '5s', mood: 'Dramatic', transition: 'Dissolve' },
      { description: 'Extreme close-up sweeping across product details — texture, logo, premium materials. Slow rack-focus shifts between individual features with motion graphics callouts', cameraAngle: 'Close-Up', duration: '8s', mood: 'Cinematic', transition: 'Match Cut' },
      { description: 'Confident professional in a modern office or studio environment uses the product naturally — genuine smile, effortless interaction, warm ambient lighting conveys lifestyle aspiration', cameraAngle: 'Medium Shot', duration: '8s', mood: 'Uplifting', transition: 'Dissolve' },
      { description: 'Wide crane shot rises to reveal the product logo large against a clean gradient background. Tagline text animates in. Ends on a lasting brand impression moment', cameraAngle: 'Crane', duration: '5s', mood: 'Dramatic', transition: 'Fade' },
    ],
  },
  {
    id: 'travel-vlog',
    name: 'Travel Vlog',
    category: 'Social Media',
    categoryColor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: 'ri-map-pin-line',
    description: 'Wanderlust-driven journey from arrival to golden-hour farewell — perfect for reels or YouTube',
    sceneCount: 5,
    scenes: [
      { description: 'Sweeping drone shot ascending over a breathtaking landscape — mountain peaks, turquoise water, or dense jungle canopy. Location title card animates in from below', cameraAngle: 'Overhead', duration: '5s', mood: 'Uplifting', transition: 'Cut' },
      { description: 'POV walk through a vibrant local market or narrow cobblestone street. Camera weaves between colorful stalls, locals, street food and authentic cultural textures', cameraAngle: 'POV', duration: '8s', mood: 'Energetic', transition: 'Whip Pan' },
      { description: 'Tight close-ups of local cuisine, artisan crafts, flowers and faces. Montage rhythm matches upbeat background music. Each shot 1-2 seconds, quick and vivid', cameraAngle: 'Close-Up', duration: '8s', mood: 'Playful', transition: 'Cut' },
      { description: 'Creator or traveler at a scenic viewpoint — golden hour light, wide smile, speaking naturally to camera or gazing at horizon. Authentic and personal connection moment', cameraAngle: 'Medium Shot', duration: '8s', mood: 'Romantic', transition: 'Dissolve' },
      { description: 'Time-lapse sunset over the destination as lights flicker on below. Silhouette of traveler in foreground. Slow fade to black as final destination text card appears', cameraAngle: 'Wide Shot', duration: '5s', mood: 'Melancholic', transition: 'Fade' },
    ],
  },
  {
    id: 'mini-documentary',
    name: 'Mini-Documentary',
    category: 'Documentary',
    categoryColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    icon: 'ri-book-open-line',
    description: 'Structured 4-act doc format with establishing context, subject interview, b-roll montage, and reflection close',
    sceneCount: 4,
    scenes: [
      { description: 'Wide establishing shot of the subject\'s environment — workshop, community, landscape or setting that defines their story. Ambient audio, natural light, unhurried pacing sets the tone', cameraAngle: 'Wide Shot', duration: '8s', mood: 'Cinematic', transition: 'Cut' },
      { description: 'Subject in natural setting speaks directly to camera or in profile. Thoughtful framing with environmental depth behind them. Authentic, unscripted feel with warm practical lighting', cameraAngle: 'Medium Shot', duration: '10s', mood: 'Dramatic', transition: 'Dissolve' },
      { description: 'B-roll montage of hands at work, tools, processes, daily rituals that define the subject\'s craft or mission. Close details reveal texture, skill and dedication', cameraAngle: 'Close-Up', duration: '10s', mood: 'Cinematic', transition: 'Match Cut' },
      { description: 'Subject alone in reflective moment — looking at horizon, holding a significant object, or quietly working. Soft music swells. Final thought or quote fades in as text. Credits follow', cameraAngle: 'Medium Shot', duration: '8s', mood: 'Melancholic', transition: 'Fade' },
    ],
  },
  {
    id: 'fashion-film',
    name: 'Fashion Film',
    category: 'Marketing',
    categoryColor: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    icon: 'ri-t-shirt-line',
    description: 'Editorial-grade fashion showcase with atmospheric opening, movement sequences, and brand close',
    sceneCount: 4,
    scenes: [
      { description: 'Misty urban environment at dawn. Model emerges from shadows into soft diffused light. Fabric moves languidly. Moody, editorial atmosphere. Slow motion at 120fps', cameraAngle: 'Wide Shot', duration: '5s', mood: 'Mysterious', transition: 'Dissolve' },
      { description: 'Dynamic tracking shot following model in motion — striding, turning, fabric catching wind. Camera circles at mid distance. Each outfit change revealed via whip-pan transition', cameraAngle: 'Tracking', duration: '10s', mood: 'Energetic', transition: 'Whip Pan' },
      { description: 'Intimate close-ups: eyes, lips, jewelry, fabric weave. Studio-lit beauty shots interspersed with textures — silk movement, embroidery detail, structured tailoring', cameraAngle: 'Close-Up', duration: '8s', mood: 'Romantic', transition: 'Dissolve' },
      { description: 'Brand logo on clean minimal background. Final look presented in architectural space — model still, commanding presence. Fade to brand identity card with collection name', cameraAngle: 'Wide Shot', duration: '5s', mood: 'Dramatic', transition: 'Fade' },
    ],
  },
  {
    id: 'tutorial',
    name: 'Tutorial / How-To',
    category: 'Explainer',
    categoryColor: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    icon: 'ri-play-circle-line',
    description: 'Clear 3-scene instructional format: intro hook, step-by-step walkthrough, and results reveal',
    sceneCount: 3,
    scenes: [
      { description: 'Enthusiastic presenter at clean desk or kitchen counter holds up finished result — the "before and after" hook. Direct eye contact with camera. Bold title card animates in', cameraAngle: 'Medium Shot', duration: '5s', mood: 'Energetic', transition: 'Cut' },
      { description: 'Overhead shot of hands demonstrating each step clearly. Materials laid out neatly. Each step labeled with animated number graphics. Tight, efficient cuts between actions', cameraAngle: 'Overhead', duration: '15s', mood: 'Uplifting', transition: 'Cut' },
      { description: 'Satisfying final reveal of completed result. Close-up then pull back to wide. Presenter reacts with genuine satisfaction. Call to action text: "Try it yourself" with subscribe prompt', cameraAngle: 'Medium Shot', duration: '5s', mood: 'Uplifting', transition: 'Fade' },
    ],
  },
  {
    id: 'event-teaser',
    name: 'Event Teaser',
    category: 'Marketing',
    categoryColor: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    icon: 'ri-calendar-event-line',
    description: 'High-energy 3-beat teaser to build anticipation before a launch, concert, or conference',
    sceneCount: 3,
    scenes: [
      { description: 'Quick-cut montage of silhouettes, partial reveals, blurred motion — teasing what\'s to come without fully showing it. Pulsing electronic score builds tension. Question mark motif', cameraAngle: 'Wide Shot', duration: '5s', mood: 'Tense', transition: 'Smash Cut' },
      { description: 'Flash of crowd energy, stage lighting, preparation behind the scenes. Faces in anticipation. Date and location text slams onto screen mid-sequence', cameraAngle: 'Medium Shot', duration: '8s', mood: 'Energetic', transition: 'Whip Pan' },
      { description: 'Single dramatic reveal frame — logo, performer silhouette, or headline speaker. All text appears: date, venue, website. Countdown timer element. Music crescendo peaks then silence', cameraAngle: 'Wide Shot', duration: '5s', mood: 'Dramatic', transition: 'Fade' },
    ],
  },
  {
    id: 'real-estate',
    name: 'Real Estate Tour',
    category: 'Marketing',
    categoryColor: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    icon: 'ri-home-line',
    description: 'Premium property showcase from exterior drone to interior walk-through to lifestyle close',
    sceneCount: 4,
    scenes: [
      { description: 'Drone approach to property from street view, ascending to reveal roofline and surroundings. Golden hour or blue sky backdrop. Property address and price tag animate in', cameraAngle: 'Overhead', duration: '8s', mood: 'Uplifting', transition: 'Dissolve' },
      { description: 'Smooth dolly through front door into main living space. Bright, airy interior. Camera reveals kitchen island, fireplace, large windows with views. Natural light and warm staging', cameraAngle: 'Dolly', duration: '10s', mood: 'Cinematic', transition: 'Cut' },
      { description: 'Room-by-room highlights: master bedroom, en-suite bathroom, outdoor terrace. Each space shown in 2-3 second beauty shots with feature callout text overlays', cameraAngle: 'Wide Shot', duration: '10s', mood: 'Uplifting', transition: 'Dissolve' },
      { description: 'Agent or owner in backyard or living room speaks to camera with confidence. Contact info, open house date and agency logo fade in. Ends on property hero shot from golden hour', cameraAngle: 'Medium Shot', duration: '8s', mood: 'Uplifting', transition: 'Fade' },
    ],
  },
  {
    id: 'food-film',
    name: 'Food / Restaurant',
    category: 'Marketing',
    categoryColor: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    icon: 'ri-restaurant-line',
    description: 'Mouth-watering culinary film from kitchen prep to plating to dining atmosphere',
    sceneCount: 4,
    scenes: [
      { description: 'Chef hands preparing fresh ingredients — chopping herbs, searing protein, tossing vegetables in a cast iron pan. Steam rises beautifully. Warm kitchen lighting. Sizzle sounds implied', cameraAngle: 'Overhead', duration: '8s', mood: 'Energetic', transition: 'Cut' },
      { description: 'Macro close-up of dish being plated artfully. Sauce drizzled in perfect arcs, garnish placed with tweezers, finishing salt scattered. Shallow depth of field, cinematic color', cameraAngle: 'Close-Up', duration: '8s', mood: 'Cinematic', transition: 'Dissolve' },
      { description: 'Finished dishes on candlelit table in beautifully styled dining room. Satisfied diners raising glasses. Ambient restaurant atmosphere — warm, inviting, high-end mood', cameraAngle: 'Wide Shot', duration: '8s', mood: 'Romantic', transition: 'Dissolve' },
      { description: 'Restaurant exterior at dusk with warm glow from windows. Name sign and logo reveal. Reservation CTA and address animate in. Ends on chef smiling in kitchen doorway', cameraAngle: 'Wide Shot', duration: '5s', mood: 'Uplifting', transition: 'Fade' },
    ],
  },
];

const CATEGORIES = ['All', 'Marketing', 'Social Media', 'Documentary', 'Explainer'];

interface PromptTemplatesLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadTemplate: (scenes: SceneItem[]) => void;
  darkMode: boolean;
}

function buildScenes(tScenes: TemplateScene[]): SceneItem[] {
  return tScenes.map((ts) => ({
    id: `scene-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    description: ts.description,
    cameraAngle: ts.cameraAngle,
    duration: ts.duration,
    mood: ts.mood,
    transition: ts.transition,
  }));
}

export default function PromptTemplatesLibrary({ isOpen, onClose, onLoadTemplate, darkMode }: PromptTemplatesLibraryProps) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  const filtered = activeCategory === 'All'
    ? TEMPLATES
    : TEMPLATES.filter(t => t.category === activeCategory);

  const handleLoad = (template: PromptTemplate) => {
    const scenes = buildScenes(template.scenes);
    onLoadTemplate(scenes);
    setLoadedId(template.id);
    setTimeout(() => {
      onClose();
      setLoadedId(null);
    }, 700);
  };

  if (!isOpen) return null;

  const overlay = darkMode ? 'bg-gray-900/95' : 'bg-white';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const sceneRow = darkMode ? 'bg-gray-900/60 border-gray-700' : 'bg-white border-gray-200';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full sm:max-w-2xl max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden border ${overlay} ${border}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-600 px-6 py-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-layout-grid-line text-white text-lg"></i>
                <h3 className="text-white font-bold text-lg">Prompt Templates</h3>
                <span className="text-white/80 text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">
                  {TEMPLATES.length} templates
                </span>
              </div>
              <p className="text-white/75 text-xs">Pre-built scene structures — load and customize for your video</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/20 text-white transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Category filter */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-white text-red-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Template list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {filtered.map(template => {
            const isExpanded = expandedId === template.id;
            const isLoaded = loadedId === template.id;
            return (
              <div
                key={template.id}
                className={`rounded-xl border overflow-hidden transition-all duration-200 ${cardBg} ${
                  isExpanded ? (darkMode ? 'border-red-700' : 'border-red-300') : ''
                }`}
              >
                {/* Template header */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : template.id)}
                  className="w-full flex items-start gap-3 p-4 text-left cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <i className={`${template.icon} text-white text-base`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {template.name}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${template.categoryColor}`}>
                        {template.category}
                      </span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                        {template.sceneCount} scenes
                      </span>
                    </div>
                    <p className={`text-xs leading-relaxed ${muted}`}>{template.description}</p>
                  </div>
                  <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-sm flex-shrink-0 mt-1 ${muted}`}></i>
                </button>

                {/* Scene preview */}
                {isExpanded && (
                  <div className={`px-4 pb-4 space-y-2 border-t ${border} pt-3`}>
                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${muted}`}>Scene Breakdown</p>
                    {template.scenes.map((scene, i) => (
                      <div key={i} className={`rounded-lg border p-2.5 flex gap-2.5 ${sceneRow}`}>
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold mt-0.5">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs leading-relaxed mb-1.5 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}
                             style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {scene.description}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {[scene.cameraAngle, scene.duration, scene.mood].map(tag => (
                              <span key={tag} className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => handleLoad(template)}
                      className={`w-full mt-1 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer flex items-center justify-center gap-2 ${
                        isLoaded
                          ? 'bg-green-500 text-white'
                          : 'bg-gradient-to-r from-red-500 to-orange-600 text-white hover:shadow-lg hover:shadow-red-500/30'
                      }`}
                    >
                      {isLoaded ? (
                        <><i className="ri-check-line"></i>Template Loaded!</>
                      ) : (
                        <><i className="ri-download-line"></i>Load This Template</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className={`px-4 py-3 border-t flex-shrink-0 ${border}`}>
          <p className={`text-[11px] text-center ${muted}`}>
            <i className="ri-information-line mr-1"></i>
            Loading a template replaces your current scenes — save your work first
          </p>
        </div>
      </div>
    </div>
  );
}
